import { useEffect, useMemo } from 'react'
import { useGLTF } from '@react-three/drei'
import * as THREE from 'three'
import { trackGeoStartLength } from '../../config/constants'
import { tunnelShaderStele, useUpdateTunnelShaderStele } from './TunnelShaderStele'
import { tunnelShader, useUpdateTunnelShader } from './TunnelShader'
import type { TrackController } from '../track/trackController'
import { useTrackController } from '../track/useTrackController'
import {
    getTransitionRanges,
    type TransitionRange,
    useTunnelTransitionVisibility,
} from './tunnelTransitionHelpers'
import { BufferGeometry, Group, Mesh } from 'three'
import { gltfAsset } from '../../assetStore'
import { disposeObject, markForDisposal } from '../../../utils/threeDisposal'
import { useSteleBreakpoint } from '@/src/hooks/useBreakpoint'

const tunnelInsideModelPath = gltfAsset.Tunnel_Inside

export function TunnelInside() {
    const { scene } = useGLTF(tunnelInsideModelPath)
    const trackController = useTrackController()
    const isStele = useSteleBreakpoint()

    if (isStele) {
        return (
            <TunnelInsideSteleContent
                scene={scene}
                trackController={trackController}
            />
        )
    }

    return (
        <TunnelInsideDefaultContent
            scene={scene}
            trackController={trackController}
        />
    )
}

type TunnelInsideContentProps = {
    scene: THREE.Group
    trackController: TrackController
}

type TunnelInsideSharedContentProps = TunnelInsideContentProps & {
    tunnelMaterial: THREE.Material
}

function TunnelInsideSteleContent({ scene, trackController }: TunnelInsideContentProps) {
    useUpdateTunnelShaderStele()
    return (
        <TunnelInsideContent
            scene={scene}
            trackController={trackController}
            tunnelMaterial={tunnelShaderStele}
        />
    )
}

function TunnelInsideDefaultContent({ scene, trackController }: TunnelInsideContentProps) {
    useUpdateTunnelShader()
    return (
        <TunnelInsideContent
            scene={scene}
            trackController={trackController}
            tunnelMaterial={tunnelShader}
        />
    )
}

function TunnelInsideContent({
    scene,
    trackController,
    tunnelMaterial,
}: TunnelInsideSharedContentProps) {
    const tunnelGroup = useMemo(
        () => createTransitionTunnelInsideGroup(scene, trackController, tunnelMaterial),
        [scene, trackController, tunnelMaterial],
    )
    useEffect(() => {
        return () => {
            disposeObject(tunnelGroup)
        }
    }, [tunnelGroup])
    useTunnelTransitionVisibility(
        tunnelGroup,
        (transition, part) =>
            part === 'inside'
                ? [`TransitionTunnelInside_transition${transition}`]
                : [],
    )
    return <primitive object={tunnelGroup} />
}

function createTransitionTunnelInsideGroup(
    tunnelAsset: THREE.Group,
    trackController: TrackController,
    tunnelMaterial: THREE.Material,
): THREE.Group {
    const sourcePart = collectSourceTunnelPart(tunnelAsset)
    if (!sourcePart.boundingBox) {
        throw new Error('Tunnel_Inside mesh is missing its bounding box.')
    }
    const bounds = sourcePart.boundingBox
    const sourceMinZ = bounds.min.z
    const sourceLength = bounds.max.z - bounds.min.z
    const tunnelGroup = new THREE.Group()
    const transitions = getTransitionRanges(trackController)
    for (const transition of transitions) {
        const geometry = createTransitionTunnelGeometry(
            sourcePart,
            sourceMinZ,
            sourceLength,
            transition,
            trackController,
        )
        const mesh = new THREE.Mesh(geometry, tunnelMaterial)
        mesh.name = `TransitionTunnelInside_${transition.key}`
        mesh.castShadow = false
        mesh.receiveShadow = false
        tunnelGroup.add(mesh)
    }
    sourcePart.dispose()

    return tunnelGroup
}

function collectSourceTunnelPart(tunnelAsset: Group): BufferGeometry {
    tunnelAsset.updateMatrixWorld(true)
    const tunnelMesh = tunnelAsset.getObjectByProperty('type','Mesh')
    if (!(tunnelMesh instanceof Mesh)) {
        throw new Error('Tunnel_Inside asset needs a mesh.')
    }
    const geometry: BufferGeometry = tunnelMesh.geometry.index
        ? tunnelMesh.geometry.toNonIndexed()
        : tunnelMesh.geometry.clone()
    geometry.applyMatrix4(tunnelMesh.matrixWorld)
    geometry.computeBoundingBox()
    return geometry
}

// TODO: this shares a lot with the code from trackBuilder.tsx
function createTransitionTunnelGeometry(
    source: THREE.BufferGeometry,
    sourceMinZ: number,
    sourceLength: number,
    transition: TransitionRange,
    trackController: TrackController,
): THREE.BufferGeometry {
    const positionAttribute = source.getAttribute('position')
    const normalAttribute = source.getAttribute('normal')
    const uvAttribute = source.getAttribute('uv')
    const transitionTileDistances = resolveTransitionTileDistances(
        transition,
        sourceLength,
        trackController,
    )
    const tileCount = transitionTileDistances.length
    const positions = new Float32Array(positionAttribute.count * tileCount * 3)
    const normals = new Float32Array(positionAttribute.count * tileCount * 3)
    const uvs = new Float32Array(positionAttribute.count * tileCount * 2)

    const localPosition = new THREE.Vector3()
    const localNormal = new THREE.Vector3()
    const worldPosition = new THREE.Vector3()
    const worldNormal = new THREE.Vector3()

    for (let tileIndex = 0; tileIndex < tileCount; tileIndex += 1) {
        const tileDistance = transitionTileDistances[tileIndex]

        for (let vertexIndex = 0; vertexIndex < positionAttribute.count; vertexIndex += 1) {
            const outputIndex = tileIndex * positionAttribute.count + vertexIndex
            localPosition.fromBufferAttribute(positionAttribute, vertexIndex)
            const progress = THREE.MathUtils.clamp(
                (localPosition.z - sourceMinZ) / sourceLength,
                0,
                1,
            )

            const frame = trackController.frameAtPosition(
                tileDistance + progress * sourceLength,
            )

            worldPosition
                .copy(frame.position)
                .addScaledVector(frame.trackRight, localPosition.x)
                .addScaledVector(frame.up, localPosition.y)

            positions[outputIndex * 3] = worldPosition.x
            positions[outputIndex * 3 + 1] = worldPosition.y
            positions[outputIndex * 3 + 2] = worldPosition.z

            localNormal
                .fromBufferAttribute(normalAttribute, vertexIndex)
                .normalize()

            worldNormal
                .copy(frame.trackRight)
                .multiplyScalar(localNormal.x)
                .addScaledVector(frame.up, localNormal.y)
                .addScaledVector(frame.tangent, localNormal.z)
                .normalize()

            normals[outputIndex * 3] = worldNormal.x
            normals[outputIndex * 3 + 1] = worldNormal.y
            normals[outputIndex * 3 + 2] = worldNormal.z

            uvs[outputIndex * 2] = uvAttribute.getX(vertexIndex)
            uvs[outputIndex * 2 + 1] = uvAttribute.getY(vertexIndex) + tileIndex
        }
    }

    const geometry = new THREE.BufferGeometry()
    markForDisposal(geometry)
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geometry.setAttribute('normal', new THREE.BufferAttribute(normals, 3))
    geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2))
    return geometry
}

function resolveTransitionTileDistances(
    transition: TransitionRange,
    tileLength: number,
    trackController: TrackController,
): number[] {
    const usableTrackLength = Math.max(
        trackController.getTrackLength() - trackGeoStartLength,
        0,
    )
    const totalTileCount = Math.max(1, Math.floor(usableTrackLength / tileLength))
    const tileDistances: number[] = []
    for (let tileIndex = 0; tileIndex < totalTileCount; tileIndex += 1) {
        const tileDistance = trackGeoStartLength + tileIndex * tileLength
        const sampleDistance = tileDistance + tileLength * 0.5
        if (sampleDistance >= transition.start && sampleDistance <= transition.end) {
            tileDistances.push(tileDistance)
        }
    }
    return tileDistances
}
